/**
 * S8 — wires the add-tile dialog's Visual source pane to the SAME SR-Edit
 * pipeline the Query Editor's Compact VQE popover uses. No parallel state:
 * `useInlineToolbarEditContext` opens its own resolver document over the
 * dialog's SQL text and commits write through the dialog's (always-mounted)
 * SqlMonaco handle, so every visual edit lands in the dialog `sql` state via
 * the editor's normal onChange.
 *
 * The dialog edits ONE statement — classification pins the cursor at offset 0
 * (`classifyTabQuery` falls back to the first statement when the cursor sits
 * outside any statement, so leading whitespace is safe).
 */
import { useMemo } from 'react';
import { getEngineForConnection } from '../../../../connections/utils/connectionLookup';
import type { ParseOptions } from '../../../../sql-grammar/model';
import type { QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import type { SqlMonacoHandle } from '../../../common/utils/sqlMonacoTypes';
import type { VqeEditContext } from '../../../visual-query-editor/edit/editContext';
import { extractStructureForContext } from '../../../visual-query-editor/edit/editContext';
import { databaseEngineIdToSqlEngine } from '../../../visual-query-editor/edit/livePullSchemaSnapshot';
import { useInlineToolbarEditContext } from '../../../visual-query-editor/inline/useInlineToolbarEditContext';
import { useCursorStatementEditInfo } from '../../../visual-query-editor/compact/useCursorStatementEditInfo';
import { buildCompactSummary } from '../../../visual-query-editor/compact/compactSummary';
import type { RenderMode } from '../../../visual-query-editor/types';
import type { VqeReadinessState } from '../../../visual-query-editor/ux/VqeReadinessBanner';

const DEFAULT_OPTIONS: ParseOptions = { engine: 'postgres' };

/** Render modes the compact surface can never edit visually (mirrors CompactVqePopover). */
const READ_ONLY_MODES: ReadonlySet<RenderMode> = new Set<RenderMode>([
  'unsupported-ddl',
  'unsupported-procedural',
  'unsupported-set-precedence',
  'unsupported-unknown',
  'parse-error',
]);

/** Synthetic cursor at the buffer start — the dialog edits the first statement. */
const FIRST_STATEMENT_RANGE = {
  startLineNumber: 1,
  startColumn: 1,
  endLineNumber: 1,
  endColumn: 1,
};

/**
 * Synthetic tab id for the dialog's resolver document + vqeStore keys
 * (banner dismissal, test registry `window.__TEST_STORES__.vqeTabServices`).
 */
export const ADD_TILE_VQE_TAB_ID = 'dashboard-add-tile';

export type AddTileVqe = {
  editContext: VqeEditContext | null;
  mode: RenderMode | null;
  summary: string;
  options: ParseOptions;
  bannerState: VqeReadinessState;
  /** Resolved structure of the current statement — feeds binding derivation. */
  structure: QueryStructure | null;
};

export function useAddTileVqe({
  editorRef,
  sql,
  connectionId,
  database,
}: {
  editorRef: React.RefObject<SqlMonacoHandle | null>;
  sql: string;
  connectionId: string;
  database?: string;
}): AddTileVqe {
  const options = useMemo<ParseOptions>(() => {
    if (!connectionId) return DEFAULT_OPTIONS;
    const engine = databaseEngineIdToSqlEngine(getEngineForConnection(connectionId));
    return engine
      ? { engine, compoundBlockAware: true, treatBareBeginAsRoutineBody: true }
      : DEFAULT_OPTIONS;
  }, [connectionId]);

  const editInfo = useCursorStatementEditInfo(sql, FIRST_STATEMENT_RANGE, options);

  const editContext = useInlineToolbarEditContext({
    editorRef,
    tabId: ADD_TILE_VQE_TAB_ID,
    query: sql,
    statementId: connectionId ? editInfo.statementId : null,
    options,
    connectionId: connectionId || null,
    database: database ?? null,
  });

  const structure = useMemo<QueryStructure | null>(() => {
    if (!editContext) return null;
    try {
      return extractStructureForContext(editContext);
    } catch {
      return null;
    }
  }, [editContext]);

  const summary = useMemo(
    () => buildCompactSummary(editInfo.mode, structure),
    [editInfo.mode, structure],
  );

  const snapshotReady = (editContext?.service.getSchemaSnapshot()?.tables.size ?? 0) > 0;
  const bannerState: VqeReadinessState = !connectionId
    ? 'no-connection'
    : editInfo.mode != null && READ_ONLY_MODES.has(editInfo.mode)
      ? 'read-only'
      : !snapshotReady
        ? 'snapshot-loading'
        : 'ready';

  return { editContext, mode: editInfo.mode, summary, options, bannerState, structure };
}
